import { useState, useEffect, useCallback } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    Alert,
    Snackbar,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    IconButton,
    Tooltip,
    Stack,
} from "@mui/material";
import {
    ExpandMore as ExpandMoreIcon,
    Save as SaveIcon,
    Refresh as RefreshIcon,
    CloudUpload as CloudUploadIcon,
} from "@mui/icons-material";
import siteSettingsService, {
    SiteSetting,
    GroupedSettings,
} from "../../services/siteSettingsService";

const TYPE_COLORS: Record<
    string,
    "default" | "primary" | "secondary" | "success" | "warning" | "info"
> = {
    text: "default",
    html: "info",
    image: "success",
    json: "warning",
    number: "primary",
    boolean: "secondary",
};

interface SiteSettingsProps {
    // Restricts the page to a single settings group — used to embed just
    // one section (e.g. "accounting") somewhere other than the full
    // E-commerce Website settings tab, reusing the same editor rather than
    // building a separate one. Omit for the normal, all-groups view.
    groupFilter?: string;
    title?: string;
    subtitle?: string;
}

export function SiteSettings({ groupFilter, title, subtitle }: SiteSettingsProps = {}) {
    const [grouped, setGrouped] = useState<GroupedSettings>({});
    const [editedValues, setEditedValues] = useState<Record<string, string>>(
        {},
    );
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: "success" | "error" | "info";
    }>({ open: false, message: "", severity: "success" });
    // Filtered to one group starts pre-expanded — no reason to make the
    // user click through an accordion covering just itself.
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
        () => (groupFilter ? new Set([groupFilter]) : new Set()),
    );
    const [uploadingKey, setUploadingKey] = useState<string | null>(null);

    const loadSettings = useCallback(async () => {
        setLoading(true);
        try {
            const data = await siteSettingsService.getGrouped();
            setGrouped(data);
            setEditedValues({});
        } catch (err) {
            setSnackbar({
                open: true,
                message: "Failed to load settings",
                severity: "error",
            });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadSettings();
    }, [loadSettings]);

    const handleValueChange = (key: string, value: string) => {
        setEditedValues((prev) => ({ ...prev, [key]: value }));
    };

    const hasChanges = Object.keys(editedValues).length > 0;

    const handleSaveAll = async () => {
        if (!hasChanges) return;
        setSaving(true);
        try {
            await siteSettingsService.bulkUpdate(editedValues);
            setSnackbar({
                open: true,
                message: `Saved ${Object.keys(editedValues).length} setting(s)`,
                severity: "success",
            });
            await loadSettings();
        } catch (err) {
            setSnackbar({
                open: true,
                message: "Failed to save settings",
                severity: "error",
            });
        } finally {
            setSaving(false);
        }
    };

    const handleSaveSingle = async (key: string) => {
        const value = editedValues[key];
        if (value === undefined) return;
        setSaving(true);
        try {
            await siteSettingsService.updateByKey(key, { value });
            setSnackbar({
                open: true,
                message: `Saved "${key}"`,
                severity: "success",
            });
            setEditedValues((prev) => {
                const next = { ...prev };
                delete next[key];
                return next;
            });
            await loadSettings();
        } catch (err) {
            setSnackbar({
                open: true,
                message: `Failed to save "${key}"`,
                severity: "error",
            });
        } finally {
            setSaving(false);
        }
    };

    const handleImageUpload = async (
        key: string,
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const file = event.target.files?.[0];
        if (!file) return;
        setUploadingKey(key);
        try {
            await siteSettingsService.uploadImage(key, file);
            setSnackbar({
                open: true,
                message: `Image uploaded for "${key}"`,
                severity: "success",
            });
            await loadSettings();
        } catch (err) {
            setSnackbar({
                open: true,
                message: `Failed to upload image for "${key}"`,
                severity: "error",
            });
        } finally {
            setUploadingKey(null);
        }
    };

    const getCurrentValue = (setting: SiteSetting): string => {
        if (editedValues[setting.key] !== undefined) {
            return editedValues[setting.key];
        }
        return setting.value || "";
    };

    const isEdited = (key: string) => editedValues[key] !== undefined;

    const sortedGroups = groupFilter
        ? Object.keys(grouped).filter((g) => g === groupFilter)
        : siteSettingsService
              .getGroupOrder()
              .filter((g) => grouped[g])
              .concat(
                  Object.keys(grouped).filter(
                      (g) => !siteSettingsService.getGroupOrder().includes(g),
                  ),
              );

    const handleAccordionToggle = (group: string) => {
        setExpandedGroups((prev) => {
            const next = new Set(prev);
            if (next.has(group)) {
                next.delete(group);
            } else {
                next.add(group);
            }
            return next;
        });
    };

    const renderSettingField = (setting: SiteSetting) => {
        const value = getCurrentValue(setting);
        const edited = isEdited(setting.key);
        if (setting.value_type === "image") {
            return (
                <Box key={setting.key} sx={{ mb: 2.5 }}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ mb: 0.5 }}
                    >
                        <Typography variant="subtitle2" fontWeight={600}>
                            {setting.label}
                        </Typography>
                        <Chip
                            label="image"
                            size="small"
                            color="success"
                            variant="outlined"
                            sx={{ height: 20, fontSize: "0.7rem" }}
                        />
                    </Stack>
                    {setting.description && (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", mb: 1 }}
                        >
                            {setting.description}
                        </Typography>
                    )}
                    <Stack direction="row" spacing={2} alignItems="center">
                        {value && (
                            <Box
                                component="img"
                                src={value}
                                alt={setting.label}
                                sx={{
                                    width: 120,
                                    height: 120,
                                    objectFit: "cover",
                                    borderRadius: 2,
                                    border: "1px solid",
                                    borderColor: "divider",
                                }}
                            />
                        )}
                        <Box>
                            <Button
                                variant="outlined"
                                component="label"
                                startIcon={
                                    uploadingKey === setting.key ? (
                                        <CircularProgress size={16} />
                                    ) : (
                                        <CloudUploadIcon />
                                    )
                                }
                                disabled={uploadingKey === setting.key}
                                size="small"
                            >
                                {value ? "Replace Image" : "Upload Image"}
                                <input
                                    type="file"
                                    hidden
                                    accept="image/*"
                                    onChange={(e) =>
                                        handleImageUpload(setting.key, e)
                                    }
                                />
                            </Button>
                            {value && (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                        display: "block",
                                        mt: 0.5,
                                        maxWidth: 250,
                                        wordBreak: "break-all",
                                    }}
                                >
                                    {value.length > 60
                                        ? value.slice(0, 60) + "..."
                                        : value}
                                </Typography>
                            )}
                        </Box>
                    </Stack>
                </Box>
            );
        }

        const isMultiline =
            setting.value_type === "json" ||
            setting.value_type === "html" ||
            value.length > 100;

        return (
            <Box key={setting.key} sx={{ mb: 2.5 }}>
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ mb: 0.5 }}
                >
                    <Typography variant="subtitle2" fontWeight={600}>
                        {setting.label}
                    </Typography>
                    <Chip
                        label={setting.value_type}
                        size="small"
                        color={TYPE_COLORS[setting.value_type] || "default"}
                        variant="outlined"
                        sx={{ height: 20, fontSize: "0.7rem" }}
                    />
                    {edited && (
                        <Chip
                            label="modified"
                            size="small"
                            color="warning"
                            sx={{ height: 20, fontSize: "0.7rem" }}
                        />
                    )}
                </Stack>
                {setting.description && (
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mb: 0.5 }}
                    >
                        {setting.description}
                    </Typography>
                )}
                <Stack direction="row" spacing={1} alignItems="flex-start">
                    <TextField
                        fullWidth
                        size="small"
                        multiline={isMultiline}
                        minRows={isMultiline ? 3 : undefined}
                        maxRows={12}
                        value={value}
                        onChange={(e) =>
                            handleValueChange(setting.key, e.target.value)
                        }
                        placeholder={`Enter ${setting.label.toLowerCase()}`}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                fontFamily:
                                    setting.value_type === "json" ||
                                    setting.value_type === "html"
                                        ? "monospace"
                                        : "inherit",
                                fontSize:
                                    setting.value_type === "json" ||
                                    setting.value_type === "html"
                                        ? "0.8rem"
                                        : "0.875rem",
                            },
                        }}
                    />
                    {edited && (
                        <Tooltip title="Save this setting">
                            <IconButton
                                color="primary"
                                onClick={() => handleSaveSingle(setting.key)}
                                disabled={saving}
                                size="small"
                            >
                                <SaveIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Stack>
            </Box>
        );
    };

    if (loading) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight={400}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, maxWidth: 1000, mx: "auto" }}>
            {/* Header */}
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 3 }}
            >
                <Box>
                    <Typography variant="h5" fontWeight={700}>
                        {title ?? "E-commerce Website Settings"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {subtitle ??
                            "Manage content displayed on the e-commerce website"}
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={loadSettings}
                        disabled={loading}
                        size="small"
                    >
                        Refresh
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={
                            saving ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : (
                                <SaveIcon />
                            )
                        }
                        onClick={handleSaveAll}
                        disabled={!hasChanges || saving}
                        size="small"
                    >
                        Save All ({Object.keys(editedValues).length})
                    </Button>
                </Stack>
            </Stack>

            {hasChanges && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    You have {Object.keys(editedValues).length} unsaved
                    change(s). Click "Save All" or save individual settings.
                </Alert>
            )}

            {/* Settings Groups */}
            {sortedGroups.map((group) => (
                <Accordion
                    key={group}
                    expanded={expandedGroups.has(group)}
                    onChange={() => handleAccordionToggle(group)}
                    sx={{
                        mb: 1,
                        "&:before": { display: "none" },
                        borderRadius: "8px !important",
                        border: "1px solid",
                        borderColor: "divider",
                        overflow: "hidden",
                    }}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                            backgroundColor: "grey.50",
                            "& .MuiAccordionSummary-content": {
                                alignItems: "center",
                                gap: 1.5,
                            },
                        }}
                    >
                        <Typography variant="subtitle1" fontWeight={600}>
                            {siteSettingsService.getGroupLabel(group)}
                        </Typography>
                        <Chip
                            label={`${grouped[group].length} settings`}
                            size="small"
                            variant="outlined"
                            sx={{ height: 22 }}
                        />
                        {grouped[group].some((s) => isEdited(s.key)) && (
                            <Chip
                                label="modified"
                                size="small"
                                color="warning"
                                sx={{ height: 22 }}
                            />
                        )}
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 2 }}>
                        {grouped[group]
                            .sort((a, b) => a.display_order - b.display_order)
                            .map(renderSettingField)}
                    </AccordionDetails>
                </Accordion>
            ))}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() =>
                    setSnackbar((prev) => ({ ...prev, open: false }))
                }
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
                <Alert
                    onClose={() =>
                        setSnackbar((prev) => ({ ...prev, open: false }))
                    }
                    severity={snackbar.severity}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
